// Pass-expiration controls — shared between the loyalty 'program' step and the
// membership 'expiry' step (SLICE-MEMBERSHIP-PASS extracted these out of
// StepProgram so both wizards drive the same UI).
//
// The control set (none / relative / fixed) is identical for both pass types.
// The only difference is the GATE: loyalty only offers expiry on a
// 'stay_completed' program (a 'reset' card recycles forever), so the caller in
// StepProgram conditionally renders ExpirationFields. A membership has no
// completion behavior — its expiry is valid in any mode — so the membership
// step renders ExpirationFields unconditionally.

import {
  type ExpirationMode,
  type ExpirationRelativeUnit,
} from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'
import { ClampedNumberInput } from './ClampedNumberInput.js'

const EXPIRATION_COPY: Record<ExpirationMode, { label: string; hint: string }> =
  {
    none: {
      label: 'Never expires',
      hint: 'The pass stays in the wallet forever.',
    },
    relative: {
      label: 'Expires after a period',
      hint: 'Each card expires a set time after the customer gets it.',
    },
    fixed: {
      label: 'Expires on a fixed date',
      hint: 'Every card expires on the same calendar date.',
    },
  }

const EXPIRATION_MODES: ExpirationMode[] = ['none', 'relative', 'fixed']
const EXPIRATION_UNITS: ExpirationRelativeUnit[] = ['days', 'months', 'years']

export function ExpirationFields({
  builder,
  readOnly,
}: {
  builder: BuilderController
  readOnly: boolean
}) {
  const { draft } = builder
  const mode = draft.expiration_mode

  // Inline guidance for the fixed-date control: nudge the owner to actually
  // pick a date (an empty "fixed" selection silently persists as "never
  // expires"), and warn on a past date (new cards would expire immediately).
  // `today` (YYYY-MM-DD, browser-local) also feeds the date input's `min`.
  const today = new Date().toISOString().slice(0, 10)
  const fixedDate = draft.expiration_fixed_date ?? ''
  const fixedIsEmpty = mode === 'fixed' && fixedDate === ''
  const fixedIsPast = mode === 'fixed' && fixedDate !== '' && fixedDate < today

  return (
    <div className="program-field-block">
      <span className="program-field-label">Pass expiration</span>
      <span className="program-field-hint">
        Expired passes move to a separate “Expired” section in Google Wallet.
        This only affects cards issued after you activate — existing cards keep
        their original terms.
      </span>
      <div className="program-radio-list">
        {EXPIRATION_MODES.map((m) => (
          <label
            key={m}
            className={`program-radio-row${mode === m ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name="expiration_mode"
              checked={mode === m}
              disabled={readOnly}
              onChange={() => {
                // Switching mode clears the other mode's fields so the draft
                // never carries a stale value the server would reject.
                if (m === 'none') {
                  builder.update({
                    expiration_mode: 'none',
                    expiration_relative_unit: null,
                    expiration_relative_amount: null,
                    expiration_fixed_date: null,
                  })
                } else if (m === 'relative') {
                  builder.update({
                    expiration_mode: 'relative',
                    expiration_relative_unit:
                      draft.expiration_relative_unit ?? 'months',
                    expiration_relative_amount:
                      draft.expiration_relative_amount ?? 12,
                    expiration_fixed_date: null,
                  })
                } else {
                  builder.update({
                    expiration_mode: 'fixed',
                    expiration_relative_unit: null,
                    expiration_relative_amount: null,
                    expiration_fixed_date: draft.expiration_fixed_date ?? '',
                  })
                }
              }}
            />
            <span className="program-radio-main">
              <span className="program-field-label">
                {EXPIRATION_COPY[m].label}
              </span>
              <span className="program-field-hint">
                {EXPIRATION_COPY[m].hint}
              </span>
            </span>
          </label>
        ))}
      </div>

      {mode === 'relative' && (
        <div className="program-expiry-relative">
          <span>Expires</span>
          <ClampedNumberInput
            className="owner-input program-expiry-amount"
            min={1}
            max={120}
            value={draft.expiration_relative_amount ?? 12}
            disabled={readOnly}
            onCommit={(v) => builder.update({ expiration_relative_amount: v })}
            ariaLabel="Expiry amount"
          />
          <select
            className="owner-input program-expiry-unit"
            value={draft.expiration_relative_unit ?? 'months'}
            disabled={readOnly}
            onChange={(e) =>
              builder.update({
                expiration_relative_unit: e.target
                  .value as ExpirationRelativeUnit,
              })
            }
            aria-label="Expiry unit"
          >
            {EXPIRATION_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <span>after the customer gets the card.</span>
        </div>
      )}

      {mode === 'fixed' && (
        <label className="program-expiry-fixed">
          <span className="program-field-label">Expiry date</span>
          <input
            type="date"
            className="owner-input"
            value={fixedDate}
            min={today}
            disabled={readOnly}
            onChange={(e) =>
              builder.update({ expiration_fixed_date: e.target.value })
            }
            aria-label="Expiry date"
          />
          {fixedIsEmpty && (
            <span className="program-field-hint">
              Pick a date — until you set one, this stays "never expires".
            </span>
          )}
          {fixedIsPast && (
            <p className="program-contrast-warning" role="status">
              That date is in the past — new cards would expire right away. Pick
              a future date.
            </p>
          )}
        </label>
      )}
    </div>
  )
}
